import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sun } from "lucide-react";
import { api, setToken, formatApiError } from "@/lib/solarApi";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CUSTOMER_TYPES = [
  { value: "residential", label: "Residential" },
  { value: "small_business", label: "Small Business" },
  { value: "office", label: "Office" },
  { value: "school", label: "School" },
  { value: "farm", label: "Farm" },
  { value: "ngo_community", label: "NGO / Community Facility" },
  { value: "industrial", label: "Industrial" },
  { value: "other", label: "Other" },
];
export { CUSTOMER_TYPES };

const fieldCls = "h-12 bg-white border-slate-300 text-slate-900 focus-visible:ring-emerald-600";

export default function SolarAuth({ onSuccess }) {
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", country: "Sierra Leone", city: "", customer_type: "residential" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = mode === "register"
        ? await api.post("/auth/register", form)
        : await api.post("/auth/login", { email: form.email, password: form.password });
      if (data.user?.role === "admin" && mode === "login") {
        setError("This is an admin account. Please use the admin dashboard.");
        return;
      }
      setToken(data.token);
      toast.success(mode === "register" ? "Account created" : "Welcome back");
      onSuccess(data.user);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="solar-auth" className="max-w-md mx-auto rounded-lg border border-slate-200 bg-white p-7 sm:p-9 shadow-lg">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-obsidian">
          <Sun className="h-5 w-5 text-volt" aria-hidden="true" />
        </span>
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">
            {mode === "register" ? "Create your free account" : "Welcome back"}
          </h2>
          <p className="text-xs text-slate-500">Your calculations and reports live in your account.</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 rounded-md border border-slate-200 p-1 bg-mist">
        {[["register", "New customer"], ["login", "Sign in"]].map(([m, label]) => (
          <button
            key={m}
            type="button"
            data-testid={`solar-auth-tab-${m}`}
            onClick={() => { setMode(m); setError(""); }}
            className={`rounded py-2.5 text-sm font-semibold transition-colors ${mode === m ? "bg-obsidian text-white" : "text-slate-600 hover:text-slate-900"}`}
          >
            {label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} data-testid="solar-auth-form" className="mt-6 space-y-4">
        {mode === "register" && (
          <div>
            <Label htmlFor="sa-name">Full name *</Label>
            <Input id="sa-name" data-testid="solar-auth-name" required minLength={2} className={fieldCls} value={form.name} onChange={set("name")} />
          </div>
        )}
        <div>
          <Label htmlFor="sa-email">Email address *</Label>
          <Input id="sa-email" type="email" data-testid="solar-auth-email" required className={fieldCls} value={form.email} onChange={set("email")} />
        </div>
        <div>
          <Label htmlFor="sa-password">Password *{mode === "register" && <span className="text-slate-400 font-normal"> (min. 8 characters)</span>}</Label>
          <Input id="sa-password" type="password" data-testid="solar-auth-password" required minLength={mode === "register" ? 8 : 1} className={fieldCls} value={form.password} onChange={set("password")} />
        </div>
        {mode === "register" && (
          <>
            <div>
              <Label htmlFor="sa-phone">WhatsApp / phone number</Label>
              <Input id="sa-phone" type="tel" data-testid="solar-auth-phone" className={fieldCls} value={form.phone} onChange={set("phone")} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sa-country">Country</Label>
                <Input id="sa-country" data-testid="solar-auth-country" className={fieldCls} value={form.country} onChange={set("country")} />
              </div>
              <div>
                <Label htmlFor="sa-city">City / town</Label>
                <Input id="sa-city" data-testid="solar-auth-city" className={fieldCls} value={form.city} onChange={set("city")} />
              </div>
            </div>
            <div>
              <Label htmlFor="sa-type">Customer type</Label>
              <Select value={form.customer_type} onValueChange={(v) => setForm((f) => ({ ...f, customer_type: v }))}>
                <SelectTrigger id="sa-type" data-testid="solar-auth-type" className={fieldCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CUSTOMER_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        {error && <p role="alert" data-testid="solar-auth-error" className="text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="solar-auth-submit"
          className="w-full rounded-md bg-obsidian px-6 py-3.5 font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-pine hover:text-volt disabled:opacity-60 disabled:hover:translate-y-0"
        >
          {busy ? "Please wait…" : mode === "register" ? "Create Account & Start" : "Sign In & Continue"}
        </button>
      </form>
      <p className="mt-5 text-center text-xs text-slate-500">
        BNM team? <Link to="/admin/login" className="text-emerald-700 hover:underline">Admin sign-in</Link>
      </p>
    </div>
  );
}
